/** My settings - how this product looks and what it tells you (ADR-0212: reached from the avatar menu,
 * its own page; the password, the passkeys and the sessions are on My profile).
 *
 * **What is real.** The colour theme applies at once and is kept in this browser (`theme.ts`). The
 * Advanced Scope 2 view is a display switch kept in this browser (`lib/displayPreferences.ts`; the
 * Scope 2 table reads it). Notifications are the in-app list (always on) and the two email choices
 * the server stores (`NotificationSettings.tsx`) - and **email is off**: the page says so in the
 * table and the switches only record what the person wants for when it is on.
 *
 * **What is not, said plainly.** Density, number format, Units, and a default site and period have no
 * endpoint and nothing that would read them, so each is a row that says "Not available yet" and not a
 * control that would be saved and ignored (the dead setting ADR-0210 removed once). A personal API
 * token is not on this page at all: no endpoint issues one, and a row for it would promise a thing
 * the product cannot give. Why each is absent is behind the info control (`account.not-yet`).
 *
 * The page is a form, so it is held to the form budget (40 words, two sentences); every explanation
 * is in `infoText.ts`, section `account`.
 */

import { Gear } from "@phosphor-icons/react";

import { useAdvancedScope2 } from "../lib/displayPreferences";
import { RequireSession } from "../shell/RequireSession";
import { THEME_CHOICES, useTheme } from "../theme";
import type { ThemeChoice } from "../theme";
import { NotificationSettings } from "./NotificationSettings";
import own from "./Profile.module.css";
import { ScreenFrame } from "./ScreenFrame";
import { NotYet, SettingsPage, SettingsRow, SettingsSection } from "./SettingsPage";
import { useScreen } from "./useScreen";
import type { Screen } from "./useScreen";

const THEME_LABEL: Record<ThemeChoice, string> = {
  light: "Light",
  dark: "Dark",
};

/** Light or dark; light is the default whatever the device prefers. Applies at once and is kept in this
 * browser only (`theme.ts` says why it is not on the account): there is nothing to save and nothing
 * the server could refuse. */
function ThemePicker() {
  const [theme, choose] = useTheme();
  return (
    <fieldset className={own.choice} data-testid="profile-theme">
      <legend className={own.choiceLegend}>Colour theme</legend>
      <p className={own.choiceNote} data-testid="profile-theme-scope">
        Saved in this browser only.
      </p>
      {THEME_CHOICES.map((value) => (
        <label key={value} className={own.choiceOption}>
          <input
            type="radio"
            name="profile-theme"
            value={value}
            checked={theme === value}
            data-testid={`profile-theme-${value}`}
            onChange={() => choose(value)}
          />
          <span className={own.choiceText}>
            <span className={own.choiceName}>{THEME_LABEL[value]}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

/** The switch that reveals the market-based Scope 2 column for a person who wants it without an
 * instrument recorded. A display preference, not a statement about what law requires. */
function AdvancedScope2({ screen }: { screen: Pick<Screen, "setMethod"> }) {
  const [on, set] = useAdvancedScope2();
  return (
    <label>
      <input
        type="checkbox"
        checked={on}
        data-testid="advanced-scope2"
        onChange={(event) => {
          set(event.target.checked);
          // The basis in force follows the choice, so the screens opened next (whose links carry the
          // address's `method`) show it; without this the switch changed a stored value and nothing else.
          screen.setMethod(event.target.checked ? "market" : "location");
        }}
      />{" "}
      Show market-based Scope 2 everywhere
    </label>
  );
}

function AccountSettingsBody() {
  const screen = useScreen();
  return (
    <ScreenFrame screen={screen} title="My settings" crumb="what this product looks like and tells you">
      <SettingsPage icon={Gear} title="My settings" label="Your preferences">
        <SettingsSection title="Notifications" infoKey="account.notifications" testId="settings-notifications">
          <NotificationSettings />
        </SettingsSection>
        <SettingsSection title="Display" infoKey="account.display" testId="settings-display">
          <ThemePicker />
          <NotYet label="Density" testId="settings-density-unavailable" />
          <NotYet label="Number format" testId="settings-number-format-unavailable" />
          <NotYet label="Units" testId="settings-unit-choice-unavailable" />
          <NotYet label="Default site" testId="settings-default-site-unavailable" />
          <NotYet label="Default period" testId="settings-default-period-unavailable" />
        </SettingsSection>
        <SettingsSection title="Advanced" infoKey="account.scope2" testId="settings-advanced">
          <SettingsRow label="Scope 2 view">
            <AdvancedScope2 screen={screen} />
          </SettingsRow>
        </SettingsSection>
      </SettingsPage>
    </ScreenFrame>
  );
}

export function AccountSettingsScreen() {
  return (
    <RequireSession>
      <AccountSettingsBody />
    </RequireSession>
  );
}
